import NearMeOutlinedIcon from '@mui/icons-material/NearMeOutlined';
import {
  Box,
  Button,
  Divider,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';

import AddAttendee from './addAttendee';

import { gradientBackground } from '../../../../assets/styles/button.style';
import CalendarIcon from '../../../../assets/svg/calendarIcon.svg';
import CountryIcon from '../../../../assets/svg/countryIcon.svg';
import LinkBlueIcon from '../../../../assets/svg/linkBlueIcon.svg?react';
import ReportTypeIcon from '../../../../assets/svg/reportTypeIcon.svg';
import UserIcon from '../../../../assets/svg/userIcon.svg';
import VideoIcon from '../../../../assets/svg/videoCameraIcon.svg';
import {
  CALL_VALUES,
  MeetingDetailItemObject,
} from '../../../../constants/meetings';
import { useToastNotificationContext } from '../../../../hooks/global/alert/useToastNotification';

interface MeetingsDetailsItemProps extends MeetingDetailItemObject {
  isWB?: boolean;
  isAdmin?: boolean;
  refresh: () => void;
  canAddAttendee: boolean;
}

const mainStyle = {
  borderRadius: '6px',
  border: '1px solid #D1D1D1',
  padding: '16px 28px 16px 20px',
};

const headerStyle = {
  fontFamily: 'Schibsted Grotesk',
  fontWeight: 500,
  fontSize: '18px',
};

const bodyTextStyle = {
  fontFamily: 'Schibsted Grotesk',
  fontWeight: 400,
  fontSize: '16px',
};
const bodyTextStyle2 = {
  fontFamily: 'Schibsted Grotesk',
  fontWeight: 400,
  fontSize: '14px',
};

const buttonStyle = {
  textTransform: 'none',
  bgcolor: '#2449FC',
  borderRadius: '50px',
  color: '#FFFFFF',
  fontFamily: 'Schibsted Grotesk',
  fontWeight: 500,
  fontSize: '16px',
  paddingX: '40px',
  maxHeight: '40px',
  '&:hover': {
    bgcolor: '#FFFFFF',
    color: '#2449FC',
    border: '1px solid #2449FC',
  },
};

const buttonStyle2 = {
  color: '#2449FC',
  fontWeight: 'normal',
  alignSelf: 'flex-start',
  textTransform: 'none',
  '&:hover': {
    color: '#2449FC',
    bgcolor: 'transparent',
  },
};

export const formatDate = (inputDate: string): string | null => {
  const dateObj = new Date(inputDate);

  if (isNaN(dateObj.getTime())) {
    return null;
  }

  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  };

  const formattedDate = new Intl.DateTimeFormat('en-US', options).format(
    dateObj,
  );

  return formattedDate;
};

const MeetingDetailItem = ({
  id,
  name,
  date,
  organizer,
  reportType,
  country,
  address,
  type,
  link,
  isWB,
  isAdmin,
  refresh,
  canAddAttendee,
}: MeetingsDetailsItemProps) => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const meetingId = queryParams.get('meetingid');
  const isReportHighlighted = meetingId === `${id}`;
  const intl = useIntl();
  const { createToastNotification } = useToastNotificationContext();
  const [addAttendee, setAddAttendee] = useState<boolean>(false);
  const selectCaseManagerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isReportHighlighted && selectCaseManagerRef.current) {
      const element = selectCaseManagerRef.current;
      const elementRect = element.getBoundingClientRect();
      const absoluteElementTop = elementRect.top + window.pageYOffset;
      const middle = absoluteElementTop - window.innerHeight / 2;
      window.scrollTo({
        top: middle,
        behavior: 'smooth', // Optional: for smooth scrolling
      });
    }
  }, [isReportHighlighted, selectCaseManagerRef]);

  const getMeetingType = (CALLTYPE: number): string => {
    if (CALLTYPE === CALL_VALUES.VIDEO) return 'Video Call';
    if (CALLTYPE === CALL_VALUES.VOICE) return 'Voice Call';
    if (CALLTYPE === CALL_VALUES.IN_PERSON) return 'In Person';
    return '';
  };

  const setOpenAddAttendeeHandler = () => {
    if (!canAddAttendee) {
      createToastNotification('Case Manager cannot be updated.', 'info');
    } else if (!isWB && canAddAttendee && isAdmin) {
      setAddAttendee(true);
    }
  };
  const setCloseAddAttendeeHandler = () => {
    setAddAttendee(false);
  };

  const goToMeetingLink = (meetingLink: string | null) => {
    if (meetingLink) {
      const urlArray = meetingLink.split('/');
      const meetingCode = urlArray[urlArray.length - 1];
      window.open(`/meetings/${meetingCode}`, '_blank');
    } else {
      createToastNotification(
        intl.formatMessage({
          defaultMessage:
            'Meeting link will be created once all attendee accepted the invite.',
        }),
        'error',
      );
    }
  };

  return (
    <Stack
      direction='row-reverse'
      justifyContent='space-between'
      alignItems='center'
      sx={{
        ...mainStyle,
        ...(isReportHighlighted && { border: '1px solid black' }),
      }}
    >
      <Stack direction='row' gap={2}>
        <Divider orientation='vertical' sx={{ height: '40px' }} />
        {type === CALL_VALUES.IN_PERSON ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Typography>
              <FormattedMessage defaultMessage='In-Person Meet' />
            </Typography>
          </Box>
        ) : (
          <Button
            sx={
              isWB
                ? {
                  ...gradientBackground,
                  padding: '0px 44px',
                }
                : buttonStyle
            }
            onClick={() => goToMeetingLink(link)}
          >
            Join
          </Button>
        )}
      </Stack>
      <Stack flexGrow={1} marginRight={3}>
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='flex-start'
        >
          <Stack direction='row' gap={4} alignItems='center'>
            <Typography sx={headerStyle}>
              <FormattedMessage defaultMessage='Meeting Details' />
            </Typography>
            <Typography sx={{ ...bodyTextStyle2, color: '#878787' }}>
              <FormattedMessage defaultMessage='Organizer:' />
              &nbsp;
              <span style={{ color: '#454545' }}>{organizer}</span>
            </Typography>
          </Stack>
          {type !== CALL_VALUES.IN_PERSON && (
            <Button
              component='a'
              sx={buttonStyle2}
              startIcon={<LinkBlueIcon />}
              onClick={() => goToMeetingLink(link)}
            >
              <FormattedMessage defaultMessage='Meet Link' />
            </Button>
          )}
        </Stack>
        <Stack
          direction={{ lg: 'row', md: 'column' }}
          justifyContent='space-between'
          gap={2.5}
        >
          <Stack
            direction='row'
            alignItems='center'
            gap={1}
            flexBasis='30%'
            ref={selectCaseManagerRef}
            sx={{
              paddingX: '8px',
              ...(addAttendee && {
                border: '1px solid black',
                borderRadius: '6px',
              }),
            }}
          >
            <Tooltip title='Who’s with you'>
              <Box component='img' src={UserIcon} />
            </Tooltip>
            {!isWB && addAttendee ? (
              <AddAttendee
                meetingId={id}
                value={name}
                open={addAttendee}
                onClose={setCloseAddAttendeeHandler}
                selectCaseManagerRef={selectCaseManagerRef?.current}
                refresh={refresh}
              />
            ) : (
              <Typography
                sx={{ ...bodyTextStyle, cursor: 'pointer' }}
                onClick={setOpenAddAttendeeHandler}
              >
                {name}
              </Typography>
            )}
          </Stack>
          <Stack direction='row' alignItems='center' gap={1} flexBasis='35%'>
            <Tooltip title='Date of meet'>
              <Box component='img' src={CalendarIcon} />
            </Tooltip>
            <Typography sx={bodyTextStyle}>{formatDate(date)}</Typography>
          </Stack>
          <Stack direction='row' alignItems='center' gap={1} flexBasis='30%'>
            <Tooltip title='Report type'>
              <Box component='img' src={ReportTypeIcon} />
            </Tooltip>
            <Typography sx={bodyTextStyle}>
              {reportType.name}
            </Typography>
          </Stack>
          {type !== CALL_VALUES.IN_PERSON && (
            <>
              <Stack
                direction='row'
                alignItems='center'
                gap={1}
                flexBasis='20%'
              >
                <Tooltip title='Country'>
                  <Box component='img' src={CountryIcon} />
                </Tooltip>
                <Typography sx={bodyTextStyle}>{country}</Typography>
              </Stack>
              <Stack
                direction='row'
                alignItems='center'
                gap={1}
                justifyContent={{ lg: 'flex-end', md: 'flex-start' }}
                flexBasis='20%'
              >
                <Tooltip title='Type of meet'>
                  <Box component='img' src={VideoIcon} />
                </Tooltip>
                <Typography sx={bodyTextStyle}>
                  {getMeetingType(type)}
                </Typography>
              </Stack>
            </>
          )}
          {type === CALL_VALUES.IN_PERSON && (
            <>
              <Stack
                direction='row'
                alignItems='center'
                gap={1}
                flexBasis='20%'
              >
                <Tooltip title='Address'>
                  <NearMeOutlinedIcon />
                </Tooltip>
                <Typography sx={bodyTextStyle}>{address}</Typography>
              </Stack>
              <Stack
                direction='row'
                alignItems='center'
                gap={1}
                justifyContent={{ lg: 'flex-end', md: 'flex-start' }}
                flexBasis='20%'
              />
            </>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default MeetingDetailItem;
